import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';

import { useCreateOfferOptionsMappingMutation } from '../../Service/offerOptionsMappingsApi';
import { OfferOptionUsageType } from '../../Types/offerOptionsMappingsTypes';
import { offerOptionUsageTypeLabels } from '../../Helpers/offerOptionUsageTypeLabels';
import { AddOptionMappingDialogProps } from './AddOptionMappingDialogTypes';

export const AddOptionMappingDialog = ({
  open,
  onClose,
  offerId,
  optionTypes,
  defaultUsageType,
}: AddOptionMappingDialogProps) => {
  const [typeId, setTypeId] = useState<number | ''>('');
  const [valueId, setValueId] = useState<number | ''>('');
  const [usageType, setUsageType] = useState<OfferOptionUsageType>(defaultUsageType);
  const [amount, setAmount] = useState<number | ''>('');
  const [percentage, setPercentage] = useState<number | ''>('');
  const [currency, setCurrency] = useState<string>('PLN');

  const [createOptionMapping, { isLoading }] = useCreateOfferOptionsMappingMutation();

  useEffect(() => {
    setUsageType(defaultUsageType);
  }, [defaultUsageType, open]);

  const handleSubmit = async () => {
    if (!typeId || !valueId) return;
    await createOptionMapping({
      offerOptionMappingId: 0,
      offerId,
      offerOptionsTypeId: typeId,
      offerOptionsValueId: valueId,
      usageType,
      percentage: percentage === '' ? null : Number(percentage),
      amount: amount === '' ? null : Number(amount),
      currency: currency || null,
    });
    onClose();
  };

  const selectedType = optionTypes.find((t) => t.id === typeId);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Dodaj opcję do oferty</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
          <FormControl fullWidth>
            <InputLabel>Typ opcji</InputLabel>
            <Select
              value={typeId}
              label="Typ opcji"
              onChange={(e) => {
                setTypeId(e.target.value as number);
                setValueId('');
              }}
            >
              {optionTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Wartość opcji</InputLabel>
            <Select
              value={valueId}
              label="Wartość opcji"
              disabled={!selectedType}
              onChange={(e) => setValueId(e.target.value as number)}
            >
              {selectedType?.offerOptionsValues?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Rodzaj</InputLabel>
            <Select
              value={usageType}
              label="Rodzaj"
              onChange={(e) => setUsageType(Number(e.target.value))}
            >
              {Object.values(OfferOptionUsageType)
                .filter((v) => typeof v === 'number')
                .map((numVal) => (
                  <MenuItem key={numVal} value={numVal}>
                    {offerOptionUsageTypeLabels[numVal as OfferOptionUsageType]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Procent (opcjonalnie)"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value === '' ? '' : Number(e.target.value))}
            type="number"
            fullWidth
          />
          <TextField
            label="Kwota (opcjonalnie)"
            value={amount}
            onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            type="number"
            fullWidth
          />
          <TextField
            label="Waluta"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !typeId || !valueId}
        >
          Dodaj opcję
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AddOptionMappingDialog;
